import React from 'react';

import { ChapterWrapper } from '~/ChapterWrapper';
import { ELink } from '~/ExternalLink';
import Footer from '~/Footer';
import { Path } from '~/paths';

// const nv_3models_intro = require('../assets/images/nv_3models_intro.png');


export const GammaFunctionMeta = {
    title: "Gamma funkce *",
    shortTitle: "Gamma funkce *",
    path: Path.gamma_function,
    element: (sectionNumber:string) => <GammaFunction sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

const TOCSpec: string[]  = [
    "gf-gamma",
];


export function GammaFunction({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={GammaFunctionMeta.title} TOCSpec={TOCSpec}>

            <i>(* volitelná kapitola)</i>

            <h2 id="gf-gamma">Gamma funkce</h2>
            <p>
            Pro gamma funkci {"$\\Gamma$"} si stačí pamatovat, že:
            </p>
            <ul>
                <li>{"$\\Gamma({\\color{green}{\\frac{1}{2}}}) = \\sqrt{\\pi}$"}</li>
                <li>{"$\\Gamma({\\color{green}{1}}) = 0! = 1$"}</li>
                <li>{"$\\Gamma(k) = (k-1)\\Gamma(k-1)$"} (rekurentní vztah - snižovnání argumentu...)</li>
            </ul>
            <p>
            Odtud snadno (*) sestavíme tabulku hodnot pro přirozená čísla {"$k$"} a "poloviční" argumenty {"$\\frac{k}{2}$"}:
            {`$$
            \\begin{array}{| c | c c c c c c c c c |} \\hline
                x          & {\\color{green}{\\frac{1}{2}}} & {\\color{green}{1}} & \\frac{3}{2} & 2 & \\frac{5}{2} & 3 & \\frac{7}{2} & 4 & \\cdots \\\\ \\hline
                \\Gamma(x) & \\sqrt{\\pi} & 1 & \\frac{1}{2}\\sqrt{\\pi} & 1 & \\frac{3}{4}\\sqrt{\\pi} & 2 & \\frac{15}{8}\\sqrt{\\pi} & 6 & \\cdots \\\\ \\hline
            \\end{array}
            $$`}
            (*) Např. {"$\\Gamma(\\frac{3}{2}) = \\frac{1}{2}\\Gamma({\\color{green}{\\frac{1}{2}}}) = \\frac{1}{2}\\sqrt{\\pi}$"}.
            </p>
            <p>
            Gamma funkce je obecně definována pro každé <strong>reálné</strong> číslo {"$k > 0$"} takto:
            {`$$
            \\begin{align*}
                \\Gamma(k) &= \\int_0^\\infty x^{k-1}e^{-x}\\text{d}x \\\\
            \\end{align*}
            $$`}
            a pro celočíselné {"$k$"} platí {"$\\Gamma(k) = (k-1)!$"} Je to tedy 'spojité dodefinování faktoriálu'; fakt/konvence,
            že oproti faktoriálu jsme posunutí o jedno doprava (tj. že {"$\\Gamma(k) \\neq k!$"}), je dán výhodností této formy
            pro řadu dalších vzathů a vlastností, což zde ale nebudeme potřebovat ani mít možnost ukázat.
            Pro více detailů např. viz <ELink href='https://en.wikipedia.org/wiki/Gamma_function'>Wikipedia</ELink>.
            </p>

            <h2 id="gf-beta">Beta funkce</h2>
            <p>
            Beta funkce {"$\\text{B}(k,l)$"} je definována jako:
            {`$$
            \\begin{align*}
                \\text{B}(k,l) &= \\int_0^1 x^{k-1}(1-x)^{l-1}\\text{d}x \\\\
            \\end{align*}
            $$`}
            a je spojena s gamma funkcí vztahem:
            {`$$
            \\begin{align*}
                \\text{B}(k,l) &= \\frac{\\Gamma(k)\\Gamma(l)}{\\Gamma(k+l)} \\\\
            \\end{align*}
            $$`}
            </p>
            <p>
            Důkaz:
            {`$$
            \\begin{align*}
                {\\color{magenta}{\\Gamma(k)\\Gamma(l)}} & = \\int_0^\\infty x^{k-1}e^{-x}\\text{d}x \\int_0^\\infty y^{l-1}e^{-y}\\,\\text{d}y = | \\text{Fubini} | = \\\\
                &= \\int_0^\\infty \\int_0^\\infty x^{k-1}y^{l-1}e^{-(x+y)}\\,\\text{d}x\\text{d}y \\\\
                & = |\\text{substituce}: t = x + y, s = \\frac{x}{x+y}, x = st, y = (1-s)t, \\text{Jakobián } t| = \\\\
                & = \\int_{s=0}^1 \\int_{t=0}^\\infty ({\\color{BrickRed}{s}}{\\color{green}{t}})^{k-1}[{\\color{BrickRed}{(1-s)}}{\\color{green}{t}}]^{l-1}e^{-t}{\\color{green}{t}}\\,\\text{d}s\\,\\text{d}t = | \\text{Fubini} | = \\\\
                & = \\int_{s=0}^1 {\\color{BrickRed}{s}}^{k-1}{\\color{BrickRed}{(1-s)}}^{l-1}\\,\\text{d}s \\int_{t=0}^\\infty {\\color{green}{t^{k-1}t^{l-1}}}e^{-t}{\\color{green}{t}}\\,\\text{d}t = \\\\
                & = \\int_{s=0}^1 s^{k-1}(1-s)^{l-1}\\,\\text{d}s \\int_{t=0}^\\infty {\\color{green}{t^{k+l-1}}}e^{-t}\\,\\text{d}t = \\\\
                & = \\text{B}(k,l){\\color{magenta}{\\Gamma(k+l)}} \\\\
                & \\\\
                & \\Rightarrow \\text{B}(k,l) = {\\color{magenta}{\\frac{\\Gamma(k)\\Gamma(l)}{\\Gamma(k+l)}}}
            \\end{align*}
            $$`}
            </p>
            <Footer/>
        </ChapterWrapper>
    );
}

